import React, { useState, useRef, useEffect } from 'react';
import { useReactToPrint } from 'react-to-print';
import Barcode from 'react-barcode';
import { motion, AnimatePresence } from 'framer-motion';

const LabelPrinterModal = ({ isOpen, onClose, selectedProducts = [], allProducts = [], onClearQueue }) => {
    const [quantities, setQuantities] = useState({});
    const [localProducts, setLocalProducts] = useState([]);
    const [scanInputValue, setScanInputValue] = useState('');
    
    // Load defaults from localStorage
    const [labelType, setLabelType] = useState(() => localStorage.getItem('labelPrinter_type') || 'item');
    const [paperFormat, setPaperFormat] = useState(() => localStorage.getItem('labelPrinter_format') === 'custom' ? 'custom' : 'a4');
    
    const [labelSize, setLabelSize] = useState(() => {
        const saved = localStorage.getItem('labelPrinter_size');
        return saved ? JSON.parse(saved) : { width: 45, height: 25 };
    });
    
    const [pageSize, setPageSize] = useState(() => {
        const saved = localStorage.getItem('labelPrinter_pageSize');
        return saved ? JSON.parse(saved) : { width: 210, height: 297 };
    });

    const componentRef = useRef();

    // Save defaults when they change
    useEffect(() => { localStorage.setItem('labelPrinter_type', labelType); }, [labelType]);
    useEffect(() => { localStorage.setItem('labelPrinter_format', paperFormat); }, [paperFormat]);
    useEffect(() => { localStorage.setItem('labelPrinter_size', JSON.stringify(labelSize)); }, [labelSize]);
    useEffect(() => { localStorage.setItem('labelPrinter_pageSize', JSON.stringify(pageSize)); }, [pageSize]);

    const handleTypeChange = (type) => {
        setLabelType(type);
        // Adjust default sizes based on type
        if (paperFormat === 'zebra') {
            if (type === 'item') setLabelSize({ width: 50, height: 30 });
            else setLabelSize({ width: 50, height: 50 });
        }
    };

    const handlePaperFormatChange = (format) => {
        const newFormat = format === 'custom' ? 'custom' : 'a4';
        setPaperFormat(newFormat);
        if (newFormat === 'a4') {
            setPageSize({ width: 210, height: 297 }); // A4 paper
        }
    };

    // Keep track of previous isOpen to only initialize on open
    const prevIsOpenRef = useRef(false);

    useEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            setLocalProducts([...selectedProducts]);
            setQuantities(prev => {
                const updated = { ...prev };
                selectedProducts.forEach(p => {
                    if (updated[p._id] === undefined) {
                        updated[p._id] = 1;
                    }
                });
                return updated;
            });
            setScanInputValue('');
        }
        prevIsOpenRef.current = isOpen;
    }, [isOpen, selectedProducts]);

    const handleScan = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            // Lector de barras ultra rápido: tomar directamente e.target.value es más seguro que el estado React
            const code = e.target.value.trim();
            if (!code) return;

            // Search by barcode or ID
            const product = allProducts.find(p => p.barcode === code || String(p._id) === code);
            if (product) {
                // Check if already in local selection
                setLocalProducts(prev => {
                    const exists = prev.some(p => String(p._id) === String(product._id));
                    if (!exists) return [product, ...prev];
                    return prev;
                });
                
                // Increment quantity
                setQuantities(prev => ({
                    ...prev,
                    [product._id]: (prev[product._id] || 0) + 1
                }));
            } else {
                // Not found
                alert('⚠️ Producto no encontrado con código: ' + code);
            }
            // Always clear inputs
            setScanInputValue(''); 
            e.target.value = '';
        }
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: 'Etiquetas',
        onAfterPrint: () => console.log('Impresión finalizada'),
        removeAfterPrint: true,
        pageStyle: `
            @page { 
                size: ${paperFormat === 'custom' ? `${pageSize.width}mm ${pageSize.height}mm` : 'A4'}; 
                margin: 5mm;
            }
            @media print {
                html, body {
                    width: ${paperFormat === 'custom' ? `${pageSize.width}mm` : '210mm'};
                    height: ${paperFormat === 'custom' ? `${pageSize.height}mm` : '297mm'};
                    margin: 0;
                    padding: 0;
                }
                body {
                    -webkit-print-color-adjust: exact;
                    print-color-adjust: exact;
                }
                .print-container {
                    display: block !important;
                    width: 100% !important;
                    height: auto !important;
                }
                .label-item, .label-shelf {
                    display: inline-flex !important;
                    vertical-align: top !important;
                    page-break-inside: avoid !important;
                    break-inside: avoid !important;
                }
            }
        `
    });


    if (!isOpen) return null;

    const totalLabels = Object.values(quantities).reduce((a, b) => a + (Number(b) || 0), 0);

    // Calcula el precio efectivo aplicando descuento del sistema
    const getEffectivePrice = (p) => {
        let price = Number(p.salePrice) || 0;
        if (p.discountType && p.discountType !== 'none' && Number(p.discountValue) > 0) {
            if (p.discountType === 'percent') {
                price = price * (1 - p.discountValue / 100);
            } else if (p.discountType === 'fixed') {
                price = Math.max(0, price - p.discountValue);
            }
        }
        return price;
    };

    const getDiscountPct = (p) => {
        const base = Number(p.salePrice) || 0;
        const final = getEffectivePrice(p);
        if (base > 0 && final < base - 0.01) {
            return Math.round(((base - final) / base) * 100);
        }
        return 0;
    };

    return (
        <AnimatePresence>
            <div style={{ position: 'fixed', inset: 0, zIndex: 100, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)' }}
                />
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    style={{
                        background: 'white',
                        borderRadius: '12px',
                        width: '900px',
                        maxWidth: '95vw',
                        height: '85vh',
                        zIndex: 101,
                        display: 'flex',
                        flexDirection: 'column',
                        boxShadow: '0 10px 25px rgba(0,0,0,0.2)'
                    }}
                >
                    {/* Header */}
                    <div style={{ padding: '20px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <h2 style={{ margin: 0 }}>🖨️ Imprimir Etiquetas ({selectedProducts.length} productos)</h2>
                        <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: '24px', cursor: 'pointer' }}>✖</button>
                    </div>

                    <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
                        {/* Sidebar Controls */}
                        <div style={{ width: '300px', borderRight: '1px solid #eee', padding: '20px', overflowY: 'auto', background: '#f9f9f9', display: 'flex', flexDirection: 'column' }}>

                            {/* Barcode Scanner Input */}
                            <div style={{ marginBottom: '20px', padding: '12px', background: '#e7f1ff', border: '2px dashed #007bff', borderRadius: '8px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px', color: '#0056b3' }}>
                                    🔍 Escanear producto...
                                </label>
                                <input
                                    type="text"
                                    autoFocus
                                    placeholder="Use el lector aquí"
                                    value={scanInputValue}
                                    onChange={e => setScanInputValue(e.target.value)}
                                    onKeyDown={handleScan}
                                    style={{
                                        width: '100%',
                                        padding: '10px',
                                        borderRadius: '6px',
                                        border: '1px solid #007bff',
                                        fontSize: '14px',
                                        outline: 'none',
                                        boxShadow: '0 0 5px rgba(0,123,255,0.2)'
                                    }}
                                />
                                <div style={{ fontSize: '11px', color: '#666', marginTop: '6px', textAlign: 'center' }}>
                                    Escanee para agregar o sumar cantidad
                                </div>
                            </div>

                            {/* Paper Format Selector */}
                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Formato de Papel</label>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <button
                                        onClick={() => handlePaperFormatChange('a4')}
                                        style={{
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: paperFormat === 'a4' ? '2px solid #28a745' : '1px solid #ccc',
                                            background: paperFormat === 'a4' ? '#d4edda' : 'white',
                                            cursor: 'pointer',
                                            fontWeight: paperFormat === 'a4' ? 'bold' : 'normal',
                                            textAlign: 'left'
                                        }}
                                    >
                                        📄 Hoja A4<br /><span style={{ fontSize: '11px', color: '#666' }}>Múltiples etiquetas</span>
                                    </button>
                                    <button
                                        onClick={() => handlePaperFormatChange('custom')}
                                        style={{
                                            padding: '10px',
                                            borderRadius: '6px',
                                            border: paperFormat === 'custom' ? '2px solid #28a745' : '1px solid #ccc',
                                            background: paperFormat === 'custom' ? '#d4edda' : 'white',
                                            cursor: 'pointer',
                                            fontWeight: paperFormat === 'custom' ? 'bold' : 'normal',
                                            textAlign: 'left'
                                        }}
                                    >
                                        ⚙️ Personalizado<br /><span style={{ fontSize: '11px', color: '#666' }}>Tamaño manual</span>
                                    </button>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <label style={{ display: 'block', fontWeight: 'bold', marginBottom: '8px' }}>Tipo de Etiqueta</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button
                                        onClick={() => handleTypeChange('item')}
                                        style={{
                                            flex: 1,
                                            padding: '8px',
                                            borderRadius: '6px',
                                            border: labelType === 'item' ? '2px solid #007bff' : '1px solid #ccc',
                                            background: labelType === 'item' ? '#e7f1ff' : 'white',
                                            cursor: 'pointer',
                                            fontWeight: labelType === 'item' ? 'bold' : 'normal'
                                        }}
                                    >
                                        🏷️ Pegatina<br /><span style={{ fontSize: '11px', color: '#666' }}>Código + Nombre</span>
                                    </button>
                                    <button
                                        onClick={() => handleTypeChange('shelf')}
                                        style={{
                                            flex: 1,
                                            padding: '8px',
                                            borderRadius: '6px',
                                            border: labelType === 'shelf' ? '2px solid #007bff' : '1px solid #ccc',
                                            background: labelType === 'shelf' ? '#e7f1ff' : 'white',
                                            cursor: 'pointer',
                                            fontWeight: labelType === 'shelf' ? 'bold' : 'normal'
                                        }}
                                    >
                                        🏪 Góndola<br /><span style={{ fontSize: '11px', color: '#666' }}>Precio Grande</span>
                                    </button>
                                </div>
                                <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Ancho (mm)</label>
                                        <input
                                            type="number"
                                            value={labelSize.width}
                                            onChange={(e) => setLabelSize({ ...labelSize, width: Number(e.target.value) })}
                                            style={{ width: '100%', padding: '6px', borderRadius: 4, border: '1px solid #ccc' }}
                                        />
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <label style={{ fontSize: '11px', fontWeight: 'bold', display: 'block', marginBottom: 2 }}>Alto (mm)</label>
                                        <input
                                            type="number"
                                            value={labelSize.height}
                                            onChange={(e) => setLabelSize({ ...labelSize, height: Number(e.target.value) })}
                                            style={{ width: '100%', padding: '6px', borderRadius: 4, border: '1px solid #ccc' }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div style={{ marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 10px 0' }}>Cantidades ({localProducts.length} ítems)</h4>
                                {localProducts.map(p => (
                                    <div key={p._id} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', alignItems: 'center', fontSize: '13px' }}>
                                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }} title={p.name}>
                                            {p.name}
                                        </div>
                                        <input
                                            type="number"
                                            min="0"
                                            value={quantities[p._id] || 0}
                                            onChange={(e) => setQuantities({ ...quantities, [p._id]: Number(e.target.value) })}
                                            style={{ width: '50px', padding: '4px', borderRadius: '4px', border: '1px solid #ccc' }}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div style={{ background: '#eef', padding: '10px', borderRadius: '6px', fontSize: '13px', color: '#333' }}>
                                Total de etiquetas a imprimir: <strong>{totalLabels}</strong>
                            </div>

                        </div>

                        {/* Preview Area */}
                        <div style={{ flex: 1, padding: '40px', background: '#555', overflowY: 'auto', display: 'flex', justifyContent: 'center' }}>

                            <div ref={componentRef} className="print-wrapper" style={{
                                background: 'white',
                                padding: '5mm',
                                width: `${pageSize.width}mm`,
                                minHeight: `${pageSize.height}mm`,
                                boxShadow: '0 0 20px rgba(0,0,0,0.5)'
                            }}>
                                <style>
                                    {`
                    @page { 
                        size: ${paperFormat === 'zebra' ? `${pageSize.width}mm ${pageSize.height}mm` : 'A4'}; 
                        margin: ${paperFormat === 'zebra' ? '0mm' : '5mm'}; 
                    }
                    html, body { 
                        margin: 0mm; 
                        padding: 0mm;
                    }
                    .print-container { 
                        display: flex; 
                        flex-wrap: wrap; 
                        gap: ${paperFormat === 'a4' ? '1mm' : '0mm'}; 
                        align-content: flex-start;
                        width: ${paperFormat === 'custom' ? `${pageSize.width}mm` : '100%'};
                        height: ${paperFormat === 'custom' ? `${pageSize.height}mm` : 'auto'};
                        box-sizing: border-box;
                        overflow: hidden;
                    }
                    
                    /* Estilos Base (Preview + Print) */
                    .label-item { 
                        width: ${labelSize.width}mm; 
                        height: ${labelSize.height}mm; 
                        border: 1px dashed #ddd; /* Visible en preview */
                        display: flex; 
                        flex-direction: column; 
                        align-items: center; 
                        justify-content: center; 
                        padding: 2mm; 
                        box-sizing: border-box; 
                        overflow: hidden;
                        page-break-inside: avoid;
                        background: white;
                    }
                    .label-shelf { 
                        width: ${labelSize.width}mm; 
                        height: ${labelSize.height}mm; 
                        border: 2px solid #000; 
                        display: flex; 
                        flex-direction: column; 
                        padding: 2mm; 
                        box-sizing: border-box; 
                        page-break-inside: avoid;
                        align-items: center;
                        justify-content: space-between;
                        background: white;
                    }

                    /* Estilos Exclusivos Impresión */
                    @media print {
                        html, body {
                            width: ${paperFormat === 'custom' ? `${pageSize.width}mm` : 'auto'};
                            height: ${paperFormat === 'custom' ? `${pageSize.height}mm` : 'auto'};
                            overflow: hidden;
                        }
                        .print-wrapper {
                            box-shadow: none !important;
                            border: none !important;
                            min-height: auto !important;
                        }
                        .print-container {
                            display: block !important;
                            width: 100% !important;
                            height: auto !important;
                            transform: scale(1) !important;
                            -webkit-transform: scale(1) !important;
                        }
                        .label-item, .label-shelf { 
                            display: inline-flex !important;
                            vertical-align: top !important;
                            page-break-inside: avoid !important;
                            break-inside: avoid !important;
                            margin: 0.5mm !important;
                        }
                        .label-item {
                            border: 1px dashed #ddd !important;
                        }
                    }
                  `}
                                </style>
                                <div className="print-container" style={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: paperFormat === 'a4' ? '1mm' : '0mm',
                                    justifyContent: 'center'
                                }}>
                                    {localProducts.flatMap(p => {
                                        const qty = quantities[p._id] || 0;
                                        return Array.from({ length: qty }).map((_, i) => (
                                            <div key={`${p._id}-${i}`} className={labelType === 'item' ? 'label-item' : 'label-shelf'}
                                                style={labelType === 'item' ? {
                                                    width: `${labelSize.width}mm`, height: `${labelSize.height}mm`,
                                                    // border handled by CSS
                                                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                                    padding: '2px', overflow: 'hidden', textAlign: 'center'
                                                } : {
                                                    width: `${labelSize.width}mm`, height: `${labelSize.height}mm`,
                                                    display: 'flex', flexDirection: 'column',
                                                    padding: '4px', overflow: 'hidden',
                                                    justifyContent: 'space-between', alignItems: 'center', textAlign: 'center'
                                                }}
                                            >
                                                {labelType === 'item' ? (
                                                    <>
                                                        <div style={{ fontSize: '10px', lineHeight: '1.1', fontWeight: 'bold', textAlign: 'center', marginBottom: '2px', maxHeight: '22px', overflow: 'hidden', width: '100%' }}>
                                                            {p.name.substring(0, 35)}
                                                        </div>
                                                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginBottom: '2px' }}>
                                                            <Barcode
                                                                value={p.barcode || p._id.substring(0, 8)}
                                                                format="CODE128"
                                                                width={1.2}
                                                                height={30}
                                                                displayValue={true}
                                                                fontSize={10}
                                                                margin={1}
                                                                renderer="svg"
                                                            />
                                                        </div>
                                                    </>
                                                ) : (
                                                    <>
                                                        <div style={{ width: '100%', fontSize: '11px', fontWeight: 'bold', lineHeight: '1.2', maxHeight: '30px', overflow: 'hidden' }}>
                                                            {p.name}
                                                        </div>
                                                        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1, margin: '2px 0' }}>
                                                            {/* Precio original tachado si hay descuento */}
                                                            {getDiscountPct(p) > 0 && (
                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '1px' }}>
                                                                    <span style={{ fontSize: '11px', color: '#999', textDecoration: 'line-through' }}>
                                                                        ${Number(p.salePrice).toFixed(2)}
                                                                    </span>
                                                                    <span style={{ fontSize: '10px', background: '#e53935', color: '#fff', borderRadius: '3px', padding: '0px 3px', fontWeight: 'bold' }}>
                                                                        -{getDiscountPct(p)}%
                                                                    </span>
                                                                </div>
                                                            )}
                                                            {/* Precio con descuento */}
                                                            <div style={{ fontSize: '24px', fontWeight: 'bold', lineHeight: '1', marginTop: '2px', color: getDiscountPct(p) > 0 ? '#e53935' : '#000' }}>
                                                                ${getEffectivePrice(p).toFixed(2)}
                                                            </div>
                                                        </div>
                                                        <div style={{ width: '100%', fontSize: '10px', color: '#111', borderTop: '1px solid #ccc', paddingTop: '2px' }}>
                                                            {p.barcode || p._id.substring(0, 8)}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        ));
                                    })}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Footer */}
                    <div style={{ padding: '20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                        {onClearQueue && (
                            <button
                                onClick={onClearQueue}
                                style={{ marginRight: 'auto', padding: '10px 20px', borderRadius: '6px', border: '1px solid #dc3545', background: '#fff', color: '#dc3545', cursor: 'pointer', fontWeight: 'bold' }}
                                title="Eliminar todos los productos de la lista de impresión"
                            >
                                🗑️ Vaciar Cola
                            </button>
                        )}
                        <button onClick={onClose} style={{ padding: '10px 20px', borderRadius: '6px', border: '1px solid #ccc', background: 'white', cursor: 'pointer' }}>Cancelar</button>

                        <button
                            onClick={handlePrint}
                            style={{ padding: '10px 20px', borderRadius: '6px', border: 'none', background: '#007bff', color: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontWeight: 'bold' }}
                            disabled={totalLabels === 0}
                        >
                            🖨️ Imprimir
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

export default LabelPrinterModal;
